import { useEffect, useRef, useState, type ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { LogOut, Menu, X } from 'lucide-react'
import type { PageId } from '../types'
import { BrandLockup } from './Brand'
import { ThemeToggle, type Theme } from './ThemeToggle'

const items: { id: PageId; label: string }[] = [
  { id: 'painel', label: 'Painel' },
  { id: 'clientes', label: 'Clientes' },
  { id: 'financeiro', label: 'Faturamento' },
  { id: 'planos', label: 'Planos' },
  { id: 'cupons', label: 'Cupons' },
  { id: 'relatorios', label: 'Relatórios' },
  { id: 'auditoria', label: 'Auditoria' },
]

const THEME_KEY = 'anotacripto-theme'
const readTheme = (): Theme => {
  try { return localStorage.getItem(THEME_KEY) === 'light' ? 'light' : 'dark' } catch { return 'dark' }
}

export function Layout({ page, setPage, logout, children }: { page: PageId; setPage: (page: PageId) => void; logout: () => void; children: ReactNode }) {
  const [mobile, setMobile] = useState(false)
  const navigation = useRef<HTMLElement>(null)
  const reducedMotion = useReducedMotion()
  const [online, setOnline] = useState(navigator.onLine)
  const [theme, setTheme] = useState<Theme>(readTheme)

  useEffect(() => {
    const root = document.documentElement
    root.dataset.theme = theme
    root.style.colorScheme = theme
    try { localStorage.setItem(THEME_KEY, theme) } catch { /* navegação privada: o tema só vale nesta sessão */ }
  }, [theme])

  useEffect(() => {
    const yes = () => setOnline(true)
    const no = () => setOnline(false)
    addEventListener('online', yes)
    addEventListener('offline', no)
    return () => {
      removeEventListener('online', yes)
      removeEventListener('offline', no)
    }
  }, [])

  useEffect(() => {
    const desktop = matchMedia('(min-width: 1281px)')
    const closeOnDesktop = () => { if (desktop.matches) setMobile(false) }
    desktop.addEventListener('change', closeOnDesktop)
    return () => desktop.removeEventListener('change', closeOnDesktop)
  }, [])

  useEffect(() => {
    if (!mobile) return
    const closeWithKeyboard = (event: KeyboardEvent) => { if (event.key === 'Escape') setMobile(false) }
    addEventListener('keydown', closeWithKeyboard)
    return () => removeEventListener('keydown', closeWithKeyboard)
  }, [mobile])

  return <div className={`app-shell page-${page}`}>
    <a className="skip-link" href="#conteudo-principal">Ir para o conteúdo</a>
    <header className="app-header">
      <button type="button" className="app-header-brand" onClick={() => { setPage('painel'); setMobile(false) }} aria-label="AnotaCripto Admin — Painel"><BrandLockup/></button>
      <span className="app-header-tools">
        <button type="button" id="app-menu-toggle" className="app-header-menu" onClick={() => setMobile(value => !value)} aria-expanded={mobile} aria-controls="admin-navigation" aria-label={mobile ? 'Fechar menu' : 'Abrir menu'}>{mobile ? <X/> : <Menu/>}</button>
      </span>
      <nav ref={navigation} id="admin-navigation" className={mobile ? 'open' : ''} aria-label="Navegação administrativa">
        <span className="app-nav-links">
          {items.map(item => <button type="button" key={item.id} className={page === item.id ? 'active' : ''} aria-current={page === item.id ? 'page' : undefined} onClick={() => { setPage(item.id); setMobile(false) }}>{item.label}</button>)}
        </span>
      </nav>
    </header>
    <footer className="app-footer">
      <div className="app-footer-profile"><span className="avatar" aria-hidden="true">A</span><strong>Administrador</strong></div>
      <ThemeToggle theme={theme} onChange={setTheme}/>
      <div className="app-footer-actions">
        <span className={`status ${online ? '' : 'offline'}`} role="status" aria-label={online ? 'Conectado' : 'Sem conexão'} title={online ? 'Conectado' : 'Sem conexão'}><i/></span>
        <button type="button" className="app-footer-logout" title="Sair" aria-label="Encerrar sessão" onClick={logout}><LogOut/><span>Sair</span></button>
      </div>
    </footer>
    <div className="workspace">
      <main className="content" id="conteudo-principal" tabIndex={-1}>
        <motion.div key={page} initial={reducedMotion ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .28, ease: [.22, 1, .36, 1] }}>{children}</motion.div>
      </main>
    </div>
  </div>
}
